import { Box } from '@mui/material'
import { MaterialReactTable } from 'material-react-table'
import { useCallback, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Loader, muiRowDragHandleProps, TableToolbar } from '@/components'
import { useCanGenerateNaming } from '@/components/UberForm/Input/Naming/useCanGenerateNaming'
import { newTableColumnPayload } from '@/constants'
import { useAppContext, useAppStore, useMrtKeyboardNavigation, useTable, useTableHandlers } from '@/hooks'
import { useAdjustTableNameUsingPostMutation } from '@/rtkApi'
import { ViewColumn } from '@/rtkApi/mddApiSliceGeneratedV2'
import { formatCodeByCase } from '@/utils'
import { logError } from '@/utils/errorHandling'

import { useViewV2TableBlurSave } from '../../hooks/useViewV2TableBlurSave'
import { useViewDataContext } from '../../ViewDataContext'
import { useViewColumnsV2 } from './useViewColumnsV2'

export const ColumnsV2 = () => {
	const { t } = useAppContext()
	const {
		node,
		columns: viewColumns,
		editMode,
		onSave,
		onColumnsChange,
		dirty,
		isSaving,
		isLoading: isColumnsLoading,
		systemNodeId,
	} = useViewDataContext()
	const containerRef = useRef<HTMLDivElement>(null)

	const { generateColumnCode } = useCanGenerateNaming(node)

	// Get isUpperCase directly from system using systemNodeId from context
	const systemsSlice = useAppStore((state) => state.system.systems)
	const letterCaseToggle = systemsSlice[systemNodeId]?.form?.letterCaseToggle
	const isUpperCase = letterCaseToggle === 'true' || letterCaseToggle === undefined
	const [adjustTableNameMutation] = useAdjustTableNameUsingPostMutation()

	const {
		isUserInteracting,
		setSelectedRow,
		setTableData,
		tableData,
		updateTableData,
		deleteRow,
		updateCellValue,
	} = useTableHandlers<ViewColumn>({
		initialData: viewColumns || [],
		// No Redux actions - we use V2 onChange
		onDataChange: onColumnsChange,
	})

	// Generate code after name is already committed so typing/focus is not blocked on the naming API
	const handleNameCommit = useCallback(
		async (nameValue: string, rowIndex: number) => {
			if (!generateColumnCode || !nameValue) return

			try {
				const response = await adjustTableNameMutation({
					utilsNameRequestDto: { name: nameValue },
				}).unwrap()
				const generatedCode = formatCodeByCase(response.result, isUpperCase)
				updateCellValue(rowIndex, 'code', generatedCode)
			} catch (error) {
				logError(error, { operation: 'ColumnsV2.generateCode' })
			}
		},
		[adjustTableNameMutation, generateColumnCode, isUpperCase, updateCellValue],
	)

	const columns = useViewColumnsV2({
		generateColumnCode,
		updateCellValue,
		isUpperCase,
		onNameCommit: handleNameCommit,
	})

	// Handler to insert a new row at a specific index
	const handleInsertRow = useCallback(
		(rowIndex: number) => {
			const newRow: ViewColumn = {
				...newTableColumnPayload,
				id: Date.now(),
				uuid: uuidv4(),
			}

			// updateTableData uses Immer, so mutate draft directly
			// rowIndex of -1 means insert at beginning (empty table case)
			const insertIndex = Math.max(0, rowIndex + 1)
			updateTableData((draft) => {
				draft.splice(insertIndex, 0, newRow)
			})
		},
		[updateTableData],
	)

	const table = useTable<ViewColumn>({
		columns,
		data: tableData,
		deleteRow,
		editMode,
		onInsertRow: handleInsertRow,
		tableId: 'view-v2-columns',
		options: {
			enableEditing: true,
			editDisplayMode: 'table',
			renderBottomToolbar: () => (
				<TableToolbar<ViewColumn>
					updateTableData={updateTableData}
					newRowPayload={newTableColumnPayload}
					tableData={tableData}
					setSelectedRow={setSelectedRow}
				/>
			),
			muiRowDragHandleProps: ({ table }) =>
				muiRowDragHandleProps({ updateTableData, table, editMode }),
			// Highlight specific cells that are missing required values + gray background in readonly
			muiTableBodyCellProps: ({ row, column }) => {
				const hasName = row.original.name && row.original.name.trim() !== ''
				const hasCode = row.original.code && row.original.code.trim() !== ''
				const hasAnyData = hasName || hasCode || row.original.description || row.original.comment || row.original.mapping
				
				const isNameMissing = !hasName && hasAnyData && column.id === 'name'
				const isCodeMissing = !hasCode && hasAnyData && column.id === 'code'
				const isMissing = isNameMissing || isCodeMissing

				// Default readonly background
				const readonlyBg = !editMode ? '#F2F2F2' : 'transparent'

				return {
					sx: isMissing
						? {
								backgroundColor: 'rgba(211, 47, 47, 0.1) !important',
								outline: '1px solid rgba(211, 47, 47, 0.5)',
								outlineOffset: '-1px',
						  }
						: {
								backgroundColor: readonlyBg,
						  },
					title: isNameMissing ? t('NAME_REQUIRED') : isCodeMissing ? t('CODE_REQUIRED') : undefined,
				}
			},
		},
	})

	// Keyboard navigation
	useMrtKeyboardNavigation({
		table,
		editMode,
		onInsertRow: handleInsertRow,
		deleteRow,
		containerRef,
		onSave,
	})

	useViewV2TableBlurSave({
		containerRef,
		editMode,
		dirty,
		isSaving,
		onSave,
	})

	// Set isUserInteracting on mount to ensure selects work properly
	// (react-select renders menu in portal outside our container)
	useEffect(() => {
		isUserInteracting.current = true
	}, [isUserInteracting])

	// Sync from parent when data is loaded or structure changes (add/remove row).
	// In edit mode, when length is unchanged we do not overwrite — reorder is done in table
	// and onColumnsChange updates parent; overwriting with stale viewColumns would undo reorder.
	useEffect(() => {
		if (!viewColumns || viewColumns.length === 0) {
			setTableData([{ ...newTableColumnPayload, id: Date.now(), uuid: uuidv4() }])
			return
		}
		if (!editMode) {
			setTableData(viewColumns)
			return
		}
		setTableData((current) =>
			current.length !== viewColumns.length ? viewColumns : current,
		)
	}, [viewColumns, editMode, setTableData])

	if (isColumnsLoading) {
		return <Loader loaded={false} />
	}

	return (
		<Box
			ref={containerRef}
			sx={{ height: '100%', width: '100%', overflow: 'auto', p: 2 }}
			tabIndex={0}
		>
			<MaterialReactTable table={table} />
		</Box>
	)
}
